import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getApiUrl } from '../utils/apiUrl';

const AboutUs = () => {
  const [lang, setLang] = useState(() => localStorage.getItem('lang') || 'en');
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [footerCopyright, setFooterCopyright] = useState('Copyright © 2026 Blue API, All rights reserved.');

  const t = (zh, en) => (lang === 'zh' ? zh : en);

  useEffect(() => {
    const handleStorageChange = () => {
      setLang(localStorage.getItem('lang') || 'en');
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // 获取关于我们内容
  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await fetch(getApiUrl('/api/homepage-config'));
        const data = await response.json();
        if (data.code === 0 && data.data) {
          setContent(data.data.about_us_content || '');
          if (data.data.footer_copyright) {
            setFooterCopyright(data.data.footer_copyright);
          }
        }
      } catch (error) {
        console.log('获取关于我们失败:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, []);

  // 默认内容
  const defaultContent = lang === 'zh' ? `
    <h1>关于我们</h1>
    <h2>公司简介</h2>
    <p>Blue API 是一家专注于 AI 接口聚合服务的科技公司。我们致力于为国内开发者和企业提供稳定、安全、实惠的 AI 模型接入服务。</p>
    <h2>我们的使命</h2>
    <p>让 AI 技术触手可及，降低 AI 应用开发的门槛，助力企业数字化转型。</p>
    <h2>核心优势</h2>
    <ul>
      <li><strong>多模型支持</strong> - 一站式接入国内主流 AI 模型，包括 DeepSeek、智谱 AI、豆包等。</li>
      <li><strong>稳定可靠</strong> - 99.9% 服务可用性，智能负载均衡，确保业务连续性。</li>
      <li><strong>价格实惠</strong> - 低于官方价格，按量付费无门槛，让 AI 服务更普惠。</li>
      <li><strong>专业支持</strong> - 7×24 小时技术支持，快速响应解决问题。</li>
    </ul>
    <h2>联系我们</h2>
    <p>邮箱：support@blueapi.com</p>
    <p>地址：北京市海淀区中关村科技园</p>
  ` : `
    <h1>About Us</h1>
    <h2>Company Introduction</h2>
    <p>Blue API is a technology company focused on AI API aggregation services. We are committed to providing stable, secure, and affordable AI model access services for domestic developers and enterprises.</p>
    <h2>Our Mission</h2>
    <p>Make AI technology accessible, lower the barrier to AI application development, and help enterprises with digital transformation.</p>
    <h2>Core Advantages</h2>
    <ul>
      <li><strong>Multi-Model Support</strong> - One-stop access to mainstream domestic AI models, including DeepSeek, Zhipu AI, Doubao, etc.</li>
      <li><strong>Stable & Reliable</strong> - 99.9% service availability with intelligent load balancing to ensure business continuity.</li>
      <li><strong>Affordable Pricing</strong> - Lower than official prices, pay-as-you-go with no minimum, making AI services more accessible.</li>
      <li><strong>Professional Support</strong> - 24/7 technical support with fast response to resolve issues.</li>
    </ul>
    <h2>Contact Us</h2>
    <p>Email: support@blueapi.com</p>
    <p>Address: Zhongguancun Science Park, Haidian District, Beijing</p>
  `;

  return (
    <div className="legal-page">
      <nav className="legal-nav">
        <div className="legal-nav-container">
          <Link to="/" className="legal-logo">
            <span>Blue API</span>
          </Link>
          <div className="legal-nav-links">
            <Link to="/">{t('返回首页', 'Back to Home')}</Link>
          </div>
        </div>
      </nav>

      <main className="legal-content">
        <div className="legal-container">
          {loading ? (
            <div className="legal-loading">{t('加载中...', 'Loading...')}</div>
          ) : content ? (
            <div className="legal-html" dangerouslySetInnerHTML={{ __html: content }} />
          ) : (
            <div className="legal-html" dangerouslySetInnerHTML={{ __html: defaultContent }} />
          )}
        </div>
      </main>

      <footer className="legal-footer">
        <p>{footerCopyright}</p>
      </footer>

      <style>{`
        .legal-page {
          min-height: 100vh;
          background: #f8fafc;
        }
        .legal-nav {
          background: #fff;
          border-bottom: 1px solid #e5e7eb;
          padding: 16px 0;
        }
        .legal-nav-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 24px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .legal-logo {
          font-size: 20px;
          font-weight: 600;
          color: #1677ff;
          text-decoration: none;
        }
        .legal-nav-links a {
          color: #6b7280;
          text-decoration: none;
          font-size: 14px;
        }
        .legal-nav-links a:hover {
          color: #1677ff;
        }
        .legal-content {
          max-width: 800px;
          margin: 0 auto;
          padding: 48px 24px;
        }
        .legal-container {
          background: #fff;
          border-radius: 12px;
          padding: 48px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }
        .legal-container h1 {
          font-size: 28px;
          font-weight: 600;
          color: #111827;
          margin-bottom: 40px;
          padding-bottom: 20px;
          border-bottom: 2px solid #e5e7eb;
        }
        .about-section {
          margin-bottom: 48px;
        }
        .about-section:last-child {
          margin-bottom: 0;
        }
        .about-section h2 {
          font-size: 20px;
          font-weight: 600;
          color: #1f2937;
          margin-bottom: 20px;
        }
        .about-section p {
          font-size: 15px;
          line-height: 2;
          color: #4b5563;
          margin-bottom: 12px;
        }
        .about-list {
          list-style: none;
          padding: 0;
        }
        .about-list li {
          margin-bottom: 24px;
          padding: 20px 24px;
          background: #f9fafb;
          border-radius: 8px;
        }
        .about-list li:last-child {
          margin-bottom: 0;
        }
        .about-list strong {
          display: block;
          font-size: 16px;
          color: #111827;
          margin-bottom: 10px;
        }
        .about-list p {
          margin: 0;
          font-size: 14px;
          line-height: 1.8;
        }
        /* 配置内容的样式 */
        .legal-html h1 {
          font-size: 28px;
          font-weight: 600;
          color: #111827;
          margin-bottom: 40px;
          padding-bottom: 20px;
          border-bottom: 2px solid #e5e7eb;
        }
        .legal-html h2 {
          font-size: 20px;
          font-weight: 600;
          color: #1f2937;
          margin-top: 48px;
          margin-bottom: 20px;
        }
        .legal-html h2:first-child {
          margin-top: 0;
        }
        .legal-html p {
          font-size: 15px;
          line-height: 2;
          color: #4b5563;
          margin-bottom: 16px;
        }
        .legal-html ul {
          margin-bottom: 20px;
          padding-left: 24px;
        }
        .legal-html li {
          font-size: 15px;
          line-height: 2;
          color: #4b5563;
          margin-bottom: 12px;
        }
        .legal-html li:last-child {
          margin-bottom: 0;
        }
        .legal-footer {
          text-align: center;
          padding: 24px;
          color: #9ca3af;
          font-size: 14px;
          border-top: 1px solid #e5e7eb;
        }
        @media (max-width: 640px) {
          .legal-container {
            padding: 24px;
          }
          .legal-container h1 {
            font-size: 24px;
          }
          .about-section h2 {
            font-size: 18px;
          }
        }
      `}</style>
    </div>
  );
};

export default AboutUs;
